import { observer } from "mobx-react-lite";
import { DatabaseIcon, FileTextIcon } from "lucide-react";

import { ParseValueConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const types = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "object", label: "JSON" },
] as const;

const validationSchema = createBlockSchema<ParseValueConfiguration>()
  .requireName()
  .addRule(required("configuration.type", "Type is required"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const ParseValueBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<ParseValueConfiguration>, "DefaultFields">) => {
    const block = useBlock<ParseValueConfiguration>();
    const isReadonly = useIsReadonly();

    const handleTypeChange = (type: string) => {
      block.setConfiguration({
        ...block.configuration,
        type: type as ParseValueConfiguration["type"],
        source: block.configuration?.source || "",
      });
    };

    return (
      <>
        <SectionGroup
          title="Parse Configuration"
          description="Configure the type of value to parse"
          icon={<FileTextIcon className="h-4 w-4" />}
        >
          <ValidatedField name="Type" path="configuration.type">
            <Select
              value={block.configuration?.type || undefined}
              onValueChange={handleTypeChange}
              disabled={isReadonly}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent>
                {types.map(typeOption => (
                  <SelectItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ValidatedField>
        </SectionGroup>

        {block.configuration?.type && (
          <SectionGroup
            title="Data Source"
            description="Select the source to parse"
            icon={<DatabaseIcon className="h-4 w-4" />}
          >
            <SingleSourceSelector
              sources={sources}
              sourceKey="source"
              validationPath="configuration.source"
              name="Source"
            />
          </SectionGroup>
        )}
      </>
    );
  },
);

export default withBlockStore<ParseValueConfiguration>(ParseValueBlockCore, {
  validationSchema,
});
